import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  Language,
  detectLanguage,
  setLanguage as storeLanguage,
  getTranslation,
  languages,
  SHARED_LANG_KEY,
  LEGACY_LANG_KEY,
} from '@/utils/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: ReturnType<typeof getTranslation>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function readCurrentLang(): Language | null {
  try {
    const shared = localStorage.getItem(SHARED_LANG_KEY) as Language | null;
    if (shared && languages.includes(shared)) return shared;
    const legacy = localStorage.getItem(LEGACY_LANG_KEY) as Language | null;
    if (legacy && languages.includes(legacy)) return legacy;
  } catch {}
  return null;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('fi');

  useEffect(() => {
    const detected = detectLanguage();
    setLanguageState(detected);

    // Sync when another tab (e.g. main site) changes localStorage
    const handleStorage = (e: StorageEvent) => {
      if (e.key === SHARED_LANG_KEY || e.key === LEGACY_LANG_KEY) {
        const next = readCurrentLang();
        if (next) setLanguageState(next);
      }
    };

    // Re-read localStorage when user returns to this tab
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const next = readCurrentLang();
        if (next) setLanguageState(next);
      }
    };

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Update document title, meta description and html lang on language change
  useEffect(() => {
    const t = getTranslation(language);
    document.title = t.meta.title;
    document.documentElement.lang = language;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t.meta.description);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    storeLanguage(lang);
  };

  const t = getTranslation(language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
